import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  readStoreThemeIsDark,
  STORE_THEME_CHANGE_EVENT,
} from '../utils/storeTheme';
import './ImageGallery.css';

const DESCRIPTION_TOOLTIP_SHOW_DELAY_MS = 600;
const DESCRIPTION_TOOLTIP_HIDE_GRACE_MS = 180;
const HOVER_CYCLE_INTERVAL_MS = 800;

const ImageGallery = ({
  images,
  alt,
  imagePositions,
  className = '',
  description,
  cycleImagesOnHover = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHoverCycling, setIsHoverCycling] = useState(false);
  const [descTooltipOpen, setDescTooltipOpen] = useState(false);
  const [descTooltipThemeDark, setDescTooltipThemeDark] = useState(readStoreThemeIsDark);
  const tooltipId = useId();
  const hoverWrapRef = useRef(null);
  const openDelayTimerRef = useRef(null);
  const hideGraceTimerRef = useRef(null);
  const isWrapHoveredRef = useRef(false);
  const isTooltipHoveredRef = useRef(false);
  const rawPos = imagePositions && imagePositions[currentImageIndex];
  const currentPosition = rawPos && String(rawPos).includes('%')
    ? rawPos
    : (rawPos === 'top' ? '50% 0%' : rawPos === 'bottom' ? '50% 100%' : '50% 50%');
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // Touch handling refs (avoid re-renders while tracking gesture)
  const touchStartXRef = useRef(null);
  const touchStartYRef = useRef(null);
  const isSwipingRef = useRef(false);

  const descriptionText = typeof description === 'string' ? description.trim() : '';
  const imageCount = Array.isArray(images) ? images.length : 0;
  const canCycleOnHover = cycleImagesOnHover && imageCount > 1;
  const hasDescription = descriptionText.length > 0;

  const clearOpenDelayTimer = () => {
    if (openDelayTimerRef.current != null) {
      clearTimeout(openDelayTimerRef.current);
      openDelayTimerRef.current = null;
    }
  };

  const clearHideGraceTimer = () => {
    if (hideGraceTimerRef.current != null) {
      clearTimeout(hideGraceTimerRef.current);
      hideGraceTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearHideGraceTimer();
    hideGraceTimerRef.current = setTimeout(() => {
      hideGraceTimerRef.current = null;
      if (!isWrapHoveredRef.current && !isTooltipHoveredRef.current) {
        setDescTooltipOpen(false);
      }
    }, DESCRIPTION_TOOLTIP_HIDE_GRACE_MS);
  };

  useEffect(() => {
    return () => {
      clearOpenDelayTimer();
      clearHideGraceTimer();
    };
  }, []);

  useEffect(() => {
    if (!hasDescription) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        clearOpenDelayTimer();
        clearHideGraceTimer();
        isWrapHoveredRef.current = false;
        isTooltipHoveredRef.current = false;
        setDescTooltipOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hasDescription]);

  useEffect(() => {
    if (!canCycleOnHover || !isHoverCycling || isDragging) return undefined;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setCurrentImageIndex((prev) => (prev === 0 ? 1 : prev));
      return undefined;
    }

    let intervalId;
    const firstSwapId = window.setTimeout(() => {
      setCurrentImageIndex((prev) => (prev + 1) % imageCount);
      intervalId = window.setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % imageCount);
      }, HOVER_CYCLE_INTERVAL_MS);
    }, 280);

    return () => {
      window.clearTimeout(firstSwapId);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [canCycleOnHover, isHoverCycling, isDragging, imageCount]);

  useEffect(() => {
    const sync = () => setDescTooltipThemeDark(readStoreThemeIsDark());
    const onStorage = (e) => {
      if (e.key === 'theme' || e.key === null) sync();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(STORE_THEME_CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(STORE_THEME_CHANGE_EVENT, sync);
    };
  }, []);

  if (!images || images.length === 0) {
    return (
      <div className={`image-gallery-container ${className}`}>
        <div className="no-image-placeholder">
          <span>📷</span>
          <p>Sin imagen</p>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Touch gesture handlers for mobile swipe
  const handleTouchStart = (event) => {
    if (!images || images.length <= 1) return;
    const touch = event.touches[0];
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    isSwipingRef.current = false;
    setIsDragging(true);
    setDragOffsetX(0);
  };

  const handleTouchMove = (event) => {
    if (!images || images.length <= 1) return;
    if (touchStartXRef.current == null || touchStartYRef.current == null) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;

    // Start swiping only on horizontal intent
    if (!isSwipingRef.current) {
      if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
        isSwipingRef.current = true;
      } else {
        return; // Let vertical scroll happen
      }
    }

    // While swiping, prevent rubber banding and update drag offset
    event.preventDefault();
    setDragOffsetX(deltaX);
  };

  const handleTouchEnd = () => {
    if (!images || images.length <= 1) {
      setIsDragging(false);
      setDragOffsetX(0);
      return;
    }

    const threshold = 50; // px required to trigger navigation
    const deltaX = dragOffsetX;

    // Reset touch tracking
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    setIsDragging(false);
    setDragOffsetX(0);

    if (!isSwipingRef.current) return;
    isSwipingRef.current = false;

    if (deltaX <= -threshold) {
      nextImage();
    } else if (deltaX >= threshold) {
      prevImage();
    }
  };

  const handleWrapMouseEnter = () => {
    const canUseHoverCycle =
      canCycleOnHover &&
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (canUseHoverCycle) {
      setIsHoverCycling(true);
    }
    if (!hasDescription) return;
    isWrapHoveredRef.current = true;
    clearHideGraceTimer();
    if (descTooltipOpen || openDelayTimerRef.current != null) return;
    openDelayTimerRef.current = setTimeout(() => {
      openDelayTimerRef.current = null;
      if (isWrapHoveredRef.current) {
        setDescTooltipOpen(true);
      }
    }, DESCRIPTION_TOOLTIP_SHOW_DELAY_MS);
  };

  const handleWrapMouseLeave = () => {
    if (canCycleOnHover) {
      setIsHoverCycling(false);
      setCurrentImageIndex(0);
    }
    if (!hasDescription) return;
    isWrapHoveredRef.current = false;
    clearOpenDelayTimer();
    scheduleClose();
  };

  const handleDescTooltipMouseEnter = () => {
    isTooltipHoveredRef.current = true;
    clearOpenDelayTimer();
    clearHideGraceTimer();
  };

  const handleDescTooltipMouseLeave = () => {
    isTooltipHoveredRef.current = false;
    scheduleClose();
  };

  const tooltipContent = (
    <>
      <p className="product-image-desc-tooltip__label">Descripción</p>
      <p className="product-image-desc-tooltip__text">{descriptionText}</p>
    </>
  );

  const showTooltip = hasDescription && descTooltipOpen;

  const descTooltipThemeClass = descTooltipThemeDark
    ? 'product-image-desc-tooltip--theme-dark'
    : 'product-image-desc-tooltip--theme-light';

  return (
    <div className={`image-gallery-container ${className}`}>
      <div
        ref={hoverWrapRef}
        className="main-image-hover-wrap"
        onMouseEnter={handleWrapMouseEnter}
        onMouseLeave={handleWrapMouseLeave}
        aria-describedby={hasDescription && descTooltipOpen ? tooltipId : undefined}
      >
        <div
          className="main-image-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={images[currentImageIndex]}
            alt={`${alt} - Imagen ${currentImageIndex + 1}`}
            className="main-image"
            style={{
              transform: `translateX(${dragOffsetX}px)`,
              transition: isDragging ? 'none' : 'transform 0.25s ease',
              objectPosition: currentPosition,
            }}
          />

          {canCycleOnHover && (
            <div className="hover-cycle-dots" aria-hidden="true">
              {images.map((_, index) => (
                <span
                  key={index}
                  className={`hover-cycle-dot ${index === currentImageIndex ? 'is-active' : ''}`}
                />
              ))}
            </div>
          )}

          {images.length > 1 && !cycleImagesOnHover && (
            <>
              <button
                className="gallery-nav-button prev-button"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                aria-label="Imagen anterior"
              >
                ‹
              </button>
              <button
                className="gallery-nav-button next-button"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                aria-label="Imagen siguiente"
              >
                ›
              </button>

              <div className="image-counter">
                {currentImageIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>

      </div>

      {showTooltip &&
        createPortal(
          <div
            id={tooltipId}
            className={`product-image-desc-tooltip product-image-desc-tooltip--fixed-viewport-center ${descTooltipThemeClass}`}
            role="tooltip"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseEnter={handleDescTooltipMouseEnter}
            onMouseLeave={handleDescTooltipMouseLeave}
          >
            {tooltipContent}
          </div>,
          document.body
        )}

      {images.length > 1 && (
        <div className="thumbnail-container">
          {images.map((image, index) => (
            <button
              key={index}
              className={`thumbnail-button ${index === currentImageIndex ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                goToImage(index);
              }}
              aria-label={`Ver imagen ${index + 1}`}
            >
              <img
                src={image}
                alt={`${alt} - Miniatura ${index + 1}`}
                className="thumbnail-image"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery; 